import React, { useCallback } from 'react';
import { Dropdown, DropdownItem } from '../../Dropdown';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { ButtonDiv, Dropdownbutton, PlanDiv, SubView, Text, View } from './styles';
import { isEmpty } from '../../../helpers/common';
import { RootReducerInterface, UpdateDataInterface } from '../../../interfaces/RootReducerInterface';
import update from '../../../update.json';
import { trackAnalyticActivity } from '../../../services/analyticsService';
import { useLocation } from 'react-router-dom';
import ButtonV2 from '../../button/buttonV2';
import { useSelector } from 'react-redux';
import { BUTTON_TYPE } from '../../../global/constants';
import { rootStore } from '../../../mobx/rootStore';

interface HelpSupportDropdownProps {
  setOpenContactUsModal?: () => void;
  isMobile?: boolean;
  isSetting?: boolean;
  onClickOpenModel?: () => void;
}

const HelpSupportDropdown: React.FC<HelpSupportDropdownProps> = ({
  setOpenContactUsModal,
  isMobile,
  isSetting,
  onClickOpenModel
}) => {
  const location = useLocation();
  const stateSelector = useSelector((state: RootReducerInterface) => state);
  const { settings: settingSelector } = stateSelector || {};
  const { subscriptionList } = settingSelector;

  //mobx store variables
  const { workspaceStore } = rootStore;
  const { workspaceTotalTask } = workspaceStore;

  const getPathName = useCallback(() => {
    const source: any = {
      '/my-tasks': 'mytask',
      '/invoices': 'invoice',
      '/timeTracking': 'time tracking',
      '/report': 'report',
      '/projects': 'project',
      '/messages': 'messages',
      '/customer': 'customer',
      '/settings': 'settings',
      '/': 'inbox'
    };
    const matchedKey = (Object.keys(source) as Array<keyof typeof source>).find((key) =>
      location.pathname.startsWith(key.toString())
    );
    return matchedKey ? source[matchedKey] : 'unknown';
  }, [location.pathname]);

  const trackSupportActivity = useCallback(
    (event: string) => {
      const path = getPathName();
      trackAnalyticActivity(`support: ${event}`, { source: `${path}` });
    },
    [getPathName]
  );

  const handleMenuItemClick = (url: string, event?: string) => {
    if (event) trackSupportActivity(event);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const onContactUsClick = useCallback(() => {
    if (setOpenContactUsModal) setOpenContactUsModal();
    trackSupportActivity('contact us clicked');
  }, [setOpenContactUsModal, trackSupportActivity]);

  return (
    <PlanDiv className='help-support-dropdown'>
      <Dropdown
        isMinWidth={210}
        modalTitle='Help & Support'
        content={
          isMobile ? (
            <ButtonDiv onClick={() => trackSupportActivity('opened')}>
              <DropdownItem
                label='Help & Support'
                iconName={'help-support-icon'}
                iconSize={isSetting ? 20 : 16}
                iconViewBox='0 0 18 18'
                iconTone='fill'
              />
            </ButtonDiv>
          ) : (
            <Dropdownbutton>
              <SVGIcon name='question-mark-icon' width='16' height='16' viewBox='0 0 16 16' className='stroke-color' />
            </Dropdownbutton>
          )
        }
        trigger='click'>
        <DropdownItem
          key={1}
          label='Help Center'
          iconName='help-center-icon'
          iconSize={16}
          iconViewBox='0 0 18 18'
          iconTone='fill'
          onClick={() =>
            handleMenuItemClick(
              'https://pixerhq.notion.site/Teamcamp-Help-Center-de92c457ecf442b7a0f3bf83ae116113',
              'help center clicked'
            )
          }
          isSuffix={
            <SVGIcon name='cross-arrow-icon' width='16' height='16' viewBox='0 0 16 16' className='stroke-color' />
          }
        />
        <DropdownItem
          key={2}
          label='API Docs'
          iconName='html-code-icon'
          iconSize={18}
          iconTone='fill'
          onClick={() => handleMenuItemClick('https://api.teamcamp.app/api-reference/introduction', 'api docs clicked')}
          isSuffix={
            <SVGIcon name='cross-arrow-icon' width='16' height='16' viewBox='0 0 16 16' className='stroke-color' />
          }
        />
        <DropdownItem
          key={3}
          label='Contact us'
          iconName='navbar-Messages-icon'
          iconSize={16}
          iconViewBox='0 0 18 18'
          onClick={onContactUsClick}
          iconTone='fill'
        />
        <Text>What's new</Text>
        {update?.length > 0 &&
          update?.map(
            (item: UpdateDataInterface) =>
              !isEmpty(item.title) && (
                <DropdownItem key={item?.redirect_url} onClick={() => handleMenuItemClick(item?.redirect_url)}>
                  <View>
                    <SubView>
                      <img src={item?.icon_url} alt={item?.title} width='18' height='18' className='stroke-color' />
                      <p>{item?.title}</p>
                    </SubView>
                  </View>
                </DropdownItem>
              )
          )}
        <DropdownItem
          key={6}
          label='Full Changelog'
          iconName='calendar-icon'
          iconSize={16}
          iconTone='fill'
          iconViewBox='0 0 18 18'
          onClick={() => handleMenuItemClick('https://www.teamcamp.app/updates', 'full changelog clicked')}
          isSuffix={
            <SVGIcon name='cross-arrow-icon' width='16' height='16' viewBox='0 0 16 16' stroke='var(--text-primary)' />
          }
        />
      </Dropdown>
      {subscriptionList && subscriptionList?.activeSubscription?.length === 0 && workspaceTotalTask >= 50 && (
        <ButtonV2
          title='Free plan'
          variant={BUTTON_TYPE.SECONDARY}
          iconName={'round-uparrow-icon'}
          className='fill-width'
          onClick={onClickOpenModel}
          size='small'
        />
      )}
    </PlanDiv>
  );
};

export default HelpSupportDropdown;
